import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Check if we should use emulators
const useEmulators = process.env.NEXT_PUBLIC_USE_EMULATORS === 'true';

// Firebase configuration
// When using emulators, we can use demo credentials
const firebaseConfig = useEmulators ? {
  // Demo configuration for emulators - these are fake but valid for emulator use
  apiKey: 'demo-api-key',
  authDomain: 'demo-project.firebaseapp.com',
  projectId: 'demo-project',
  storageBucket: 'demo-project.appspot.com',
  messagingSenderId: '123456789',
  appId: '1:123456789:web:abcdef',
} : {
  // Production configuration from environment variables
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

// Check if Firebase is properly configured
export const isFirebaseConfigured = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.storageBucket
  );
};

// Initialize Firebase (singleton pattern)
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize Firebase Authentication
export const auth = getAuth(app);

// Initialize Firestore
export const db = getFirestore(app);

// Initialize Firebase Storage
export const firebaseStorage = getStorage(app);

// Initialize Firebase Functions
export const functions = getFunctions(app);

// Connect to emulators if configured
// Note: This should only run once, on client side
if (typeof window !== 'undefined' && useEmulators) {
  // Check if already connected to avoid duplicate connections
  const emulatorConnected = (window as any).__FIREBASE_EMULATORS_CONNECTED__;

  if (!emulatorConnected) {
    console.log('🔧 Connecting to Firebase Emulators...');

    try {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
      connectStorageEmulator(firebaseStorage, '127.0.0.1', 9199);
      connectFunctionsEmulator(functions, '127.0.0.1', 5001);

      (window as any).__FIREBASE_EMULATORS_CONNECTED__ = true;
      console.log('✅ Connected to Firebase Emulators');
    } catch (error) {
      console.error('❌ Failed to connect to emulators:', error);
    }
  }
}

// Export app for analytics
export { app };

export default app;
